import { Card } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AddressSourceEnum,
  IContactAddressDataSchema,
  IOneTimeInvoiceFormSchemaWithOfferings,
  IQuoteContactAddressDataSchema,
  QuoteContactAddressDataSchema,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { useGetAccountById } from '../../../../api/accountsService';
import {
  MCard,
  MDivider,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
  MVStack,
} from '../../../../components/Monetize';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import { QuoteContactDrawerSelect } from '../../../Quotes/Quote/components/contacts/QuoteContactDrawerSelect';
import QuoteContactsAddressList from '../../../Quotes/Quote/components/contacts/QuoteContactsAddressList';
import { appGlobalDataState } from '../../../../store/global.store';
import { useOneTimeInvoice } from './oneTimeInvoiceContext';

interface OneTimeInvoiceContactsDrawerContactsProps {
  canUpdateContact: boolean;
}

/**
 * Contacts tab of the invoice contacts drawer. Mirrors the quote's tab, minus
 * the linked/secondary account selects which do not apply to an invoice.
 */
export const OneTimeInvoiceContactsDrawerContacts = ({
  canUpdateContact,
}: OneTimeInvoiceContactsDrawerContactsProps) => {
  const {
    accountId,
    addressFormat,
    shippingContact,
    billingContact,
    handleChangeContact,
  } = useOneTimeInvoice();
  const { getValues, setValue } =
    useFormContext<IOneTimeInvoiceFormSchemaWithOfferings>();
  const { hasCrmConfigured } = useAtomValue(appGlobalDataState);
  const { data: accountDetails } = useGetAccountById(accountId, {
    enabled: !!accountId,
  });

  // The reused address components work on a flat form, while the invoice nests
  // these values under `addressInformation`. The two address source enums carry
  // the same members, so the values map across unchanged.
  const addressDataForm = useForm<IQuoteContactAddressDataSchema>({
    mode: 'onChange',
    resolver: zodResolver(QuoteContactAddressDataSchema),
    defaultValues: getValues(
      'addressInformation',
    ) as unknown as IQuoteContactAddressDataSchema,
  });

  const addressSource = addressDataForm.watch('addressSource');
  const billingAddressId = addressDataForm.watch('billingAddressId');
  const shippingAddressId = addressDataForm.watch('shippingAddressId');

  // Push edits back onto the invoice form. Non-initial so merely opening the
  // drawer does not dirty the form.
  useNonInitialEffect(() => {
    setValue(
      'addressInformation',
      {
        addressSource: addressSource as unknown as AddressSourceEnum,
        billingAddressId,
        shippingAddressId,
      } satisfies IContactAddressDataSchema,
      { shouldDirty: true, shouldValidate: true },
    );
  }, [addressSource, billingAddressId, shippingAddressId, setValue]);

  const showAddressOnContact =
    addressSource === QuoteSettingsDefaultAddressSourceEnum.CONTACT;

  return (
    <>
      <MCard variant="borderless">
        <Card.Body py="2">
          <MVStack gap={2}>
            <QuoteContactDrawerSelect
              label="Shipping Contact"
              value={shippingContact}
              onChange={(contact) => handleChangeContact(contact, 'primary')}
              accountId={accountId}
              canUpdateContact={canUpdateContact}
              showAddress={showAddressOnContact}
              addressFormat={addressFormat}
            />
            <MDivider />
            <QuoteContactDrawerSelect
              label="Billing Contact"
              value={billingContact}
              onChange={(contact) => handleChangeContact(contact, 'billing')}
              accountId={accountId}
              canUpdateContact={canUpdateContact}
              showAddress={showAddressOnContact}
              addressFormat={addressFormat}
            />
          </MVStack>
        </Card.Body>
      </MCard>

      {hasCrmConfigured && (
        <MCard variant="borderless">
          <Card.Body py="2">
            <MText fontSize="sm" fontWeight="600">
              Address Sources
            </MText>
            <MText my="1" fontSize="sm">
              Select the source of Shipping and Billing Addresses.
            </MText>
            <Controller
              name="addressSource"
              control={addressDataForm.control}
              render={({ field: { value, onChange, ...rest } }) => (
                <MRadioGroup value={value} onChange={onChange} {...rest}>
                  <MStack gap={1} direction="column" color="tGray.darkPurple">
                    <MRadio
                      value={QuoteSettingsDefaultAddressSourceEnum.ACCOUNT}
                      fontSize="sm"
                      onChange={() => {
                        addressDataForm.setValue(
                          'billingAddressId',
                          accountDetails?.billingAddress?.id,
                          { shouldDirty: true },
                        );
                        addressDataForm.setValue(
                          'shippingAddressId',
                          accountDetails?.shippingAddress?.id,
                          { shouldDirty: true },
                        );
                      }}
                    >
                      Source from Account Addresses
                    </MRadio>
                    <MRadio
                      value={QuoteSettingsDefaultAddressSourceEnum.CONTACT}
                      fontSize="sm"
                      onChange={() => {
                        addressDataForm.setValue('billingAddressId', '', {
                          shouldDirty: true,
                        });
                        addressDataForm.setValue('shippingAddressId', '', {
                          shouldDirty: true,
                        });
                      }}
                    >
                      Source from Contacts
                    </MRadio>
                  </MStack>
                </MRadioGroup>
              )}
            />
          </Card.Body>
        </MCard>
      )}

      {addressSource === QuoteSettingsDefaultAddressSourceEnum.ACCOUNT && (
        <QuoteContactsAddressList
          billingAccountId={accountId}
          shippingAccountId={accountId}
          addressDataForm={addressDataForm}
          addressFormat={addressFormat}
        />
      )}
    </>
  );
};
